/**
 * File Operations Utilities
 * Extracted from useNodeOperations.ts to eliminate duplication
 * Handles file attachment operations for nodes
 */

import type { ProjectData, NodeOperation, FileAttachment } from '@/shared/types/projectData';

/**
 * Attach file to a node
 * Links a file to a node's attachments list
 */
export function createAttachFileOperation(
  projectData: ProjectData,
  nodeId: string,
  fileId: string,
  fileData?: FileAttachment
): NodeOperation | null {
  if (!projectData) return null;

  const node = projectData.nodes.find(n => n.id === nodeId);
  if (!node) return null;

  return {
    type: 'attach-file',
    nodeId,
    fileId,
    fileData,
    timestamp: Date.now()
  };
}

/**
 * Detach file from a node
 * Removes a file from a node's attachments list
 */
export function createDetachFileOperation(
  projectData: ProjectData,
  nodeId: string,
  fileId: string
): NodeOperation | null {
  if (!projectData) return null;

  const node = projectData.nodes.find(n => n.id === nodeId);
  if (!node) return null;

  // Find the file data for undo
  const fileData = projectData.files?.find(f => f.id === fileId);

  return {
    type: 'detach-file',
    nodeId,
    fileId,
    fileData, // Store for undo
    timestamp: Date.now()
  };
}

/**
 * Delete file from project
 * Removes file and detaches from all nodes
 */
export function createDeleteFileOperation(
  projectData: ProjectData,
  fileId: string
): NodeOperation | null {
  if (!projectData) return null;

  // Find the file data for undo
  const fileData = projectData.files?.find(f => f.id === fileId);
  if (!fileData) return null;

  // Metadata cannot restore file bytes. Do not advertise this local deletion
  // as undoable until the file service exposes a supported restore operation.
  return {
    type: 'delete-file',
    fileId,
    skipHistory: true,
    timestamp: Date.now()
  };

}

/**
 * Rename file
 * Updates file name in project files list
 */
export function createRenameFileOperation(
  projectData: ProjectData,
  fileId: string,
  newName: string
): NodeOperation | null {
  if (!projectData) return null;

  // Find the file data for undo
  const fileData = projectData.files?.find(f => f.id === fileId);
  if (!fileData) return null;

  return {
    type: 'rename-file',
    fileId,
    previousFileName: fileData.originalName,
    newFileName: newName,
    timestamp: Date.now()
  };
}
